import { StyleProp, ViewStyle } from 'react-native';
import { MediaType } from '../graphql/enums';
import { MediaData } from './domain';
import { Media } from './domain/media';
import { ReactNode } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

export type ImageUploadArgs = {
  onStart?: (image: Image) => void;
  onProgress?: (id: string, progress: number) => void;
  onComplete?: (image: Image) => void;
};

export type MediaUploadArgs = {
  onStart?: (mediaItem: MediaItem) => void;
  onProgress?: (mediaId: string, progress: number) => void;
  onComplete?: (mediaId: string) => void;
  onError?: (error: unknown, mediaId: string) => void;
};

export type Image = {
  id: string;
  url: string;
  extension?: string;
  imageData?: string;
};

export type Dimensions = {
  width: number | null;
  height: number | null;
};

export interface MediaItem extends Media {
  name: string;
  url: string;
}

export type MediaPickerProps = {
  /** Retained native conversations disable new picker/upload work while rechecking. */
  active?: boolean;
  setMedia: (mediaData: MediaData) => void;
  setProgress: (mediaId: string, progress: number) => void;
  setUploading?: (uploading: boolean) => void;
  /** Remove a preview whose transfer failed; the picker reports the error. */
  onUploadError?: (mediaId: string) => void;
  acceptedMediaTypes?: MediaType[];
  notifyCameraVisible?: (cameraVisible: boolean) => void;
  dismissKeyboard?: () => void;
  mode?: PickerMode;
  style?: StyleProp<ViewStyle>;
  iconColor?: string;
  workspaceId?: string;
  icon?: string;
  iconSize?: number;
  text?: string;
  cardStyle?: 'elevated' | 'outlined' | 'contained';
  isIconInBox?: boolean;
  customTrigger?: React.ReactNode;
  /** A button component that accepts standard press/children props (e.g. from @gripp/shared-ui Button) */
  buttonComponent?: React.ReactElement<Record<string, unknown>>;
  onPickerReady?: (openPicker: () => void) => void;
  /**
   * Copy the capture to the device instead of uploading it, reporting the
   * client media id; the offline outbox uploads it later. Only the routine
   * form sets this, and only where the outbox can take the submission.
   */
  captureLocally?: boolean;
};

export type MediaPickerComponent = (props: MediaPickerProps) => ReactNode;

export enum PickerMode {
  CAMERA,
  PICKER,
  ATTACHMENT,
}

export type SignatureScreenProps = {
  setValue: UseFormSetValue<FieldValues>;
  fieldName: string;
  toggleProcessing?: (processing: boolean) => void;
  /** Keep the signature on the device for the outbox to upload; see `MediaPickerProps.captureLocally`. */
  captureLocally?: boolean;
};

export type SignatureScreenComponent = (
  props: SignatureScreenProps
) => ReactNode;

export type BarcodeScannerProps = {
  onScanned: (value: string) => void;
  onDismiss: () => void;
};

export type BarcodeScannerComponent = (props: BarcodeScannerProps) => ReactNode;
